import * as React from "react";
import { BarChart3 } from "lucide-react";
import { PostDetailsDialog } from "@/components/PostDialogs/PostDetailsDialog";
import { Button, ButtonGroup, SimpleSelect, Skeleton } from "@/components/ui";
import { averageTooltip } from "@/components/PostsStats/averageCopy";
import { socialIcons, socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { useDesignPackageStats } from "../hooks/useDesignPackageStats";
import {
  formatCount,
  groupByPost,
  PerfMetric,
  PostAggregate,
  sortRecent,
  sortTop,
  UsageSummary,
  usageSummary,
} from "../performance";
import { AllPostsDialog } from "./AllPostsDialog";
import { PostPerformanceCard } from "./PostPerformanceCard";
import { StatTile } from "./StatTile";

export interface PerformanceSectionProps {
  pkg: DesignPackageDetail;
  /** Absent when the viewer can't post for players. */
  onPostForPlayer?: () => void;
}

type PerfMode = "recent" | "top";

export const PERF_MODE_STORAGE_KEY = "designStudio.perfMode";
const MAX_POSTS = 8;
const METRICS: PerfMetric[] = ["impressions", "reactions", "comments"];

const METRIC_ITEMS = [
  { value: "impressions", label: "by Impressions" },
  { value: "reactions", label: "by Reactions" },
  { value: "comments", label: "by Comments" },
];

function readStoredMode(): { mode: PerfMode; metric: PerfMetric } {
  const fallback = { mode: "recent" as PerfMode, metric: "impressions" as PerfMetric };
  try {
    const raw = window.sessionStorage.getItem(PERF_MODE_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { mode?: unknown; metric?: unknown };
    return {
      mode: parsed.mode === "top" ? "top" : "recent",
      metric: METRICS.includes(parsed.metric as PerfMetric) ? (parsed.metric as PerfMetric) : "impressions",
    };
  } catch {
    return fallback;
  }
}

function writeStoredMode(value: { mode: PerfMode; metric: PerfMetric }): void {
  try {
    window.sessionStorage.setItem(PERF_MODE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the choice just won't persist.
  }
}

function PerformanceSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3" data-testid="performance-loading">
      <Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="col-span-2 h-[62px] rounded-xl" data-testid="stat-tile-skeleton" />
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} className="h-[62px] rounded-xl" data-testid="stat-tile-skeleton" />
        ))}
      </div>
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} className="h-[74px] rounded-xl" data-testid="post-row-skeleton" />
        ))}
      </div>
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${formatCount(n)} ${n === 1 ? one : many}`;

function NetworkChips({ byNetwork }: { byNetwork: UsageSummary["byNetwork"] }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600 tabular-nums" data-testid="network-chips">
      {byNetwork.map(({ network, count }) => (
        <span
          key={network}
          className="flex items-center gap-1"
          title={`${plural(count, "post", "posts")} on ${socialNetworkNames[network]}`}
        >
          <img src={socialIcons[network]} className="size-4" alt={socialNetworkNames[network]} />
          {formatCount(count)}
        </span>
      ))}
    </div>
  );
}

function PerformanceEmptyState({
  published,
  scheduled,
  onPostForPlayer,
}: {
  published: boolean;
  scheduled: number;
  onPostForPlayer?: () => void;
}): JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center"
      data-testid="performance-empty"
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
        <BarChart3 className="size-5" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-slate-800">No posts yet</p>
        <p className="text-sm text-slate-500">
          {scheduled > 0
            ? `${plural(scheduled, "scheduled post shows", "scheduled posts show")} up here once ${
                scheduled === 1 ? "it goes" : "they go"
              } live.`
            : published
            ? "Players see this design in their library and can share it any time."
            : "Posts you make for a player show up here once they go live."}
        </p>
      </div>
      {onPostForPlayer && (
        <Button variant="outline" size="sm" className="mt-1" onClick={onPostForPlayer}>
          Post for a player
        </Button>
      )}
    </div>
  );
}

export function PerformanceSection({ pkg, onPostForPlayer }: PerformanceSectionProps): JSX.Element {
  const stats = useDesignPackageStats(pkg.id);
  const [{ mode, metric }, setView] = React.useState(readStoredMode);
  const [openPost, setOpenPost] = React.useState<{ postId: number; socialNetworkId: SocialNetworksId } | null>(null);
  const [allPostsOpen, setAllPostsOpen] = React.useState(false);
  // A post opened from the table goes back to the table when it closes.
  const [returnToAllPosts, setReturnToAllPosts] = React.useState(false);

  const updateView = (next: { mode: PerfMode; metric: PerfMetric }) => {
    setView(next);
    writeStoredMode(next);
  };

  const aggregates = React.useMemo(() => groupByPost(stats.data?.breakdown ?? []), [stats.data]);
  const usage = React.useMemo(() => usageSummary(stats.data?.breakdown ?? []), [stats.data]);
  const posts = React.useMemo(
    () => (mode === "top" ? sortTop(aggregates, metric) : sortRecent(aggregates)).slice(0, MAX_POSTS),
    [aggregates, mode, metric]
  );

  if (stats.isLoading) return <PerformanceSkeleton />;

  if (stats.isError) {
    return (
      <div className="flex flex-col items-start gap-1 py-4 text-sm text-slate-600" data-testid="performance-error">
        <p>Couldn't load performance.</p>
        <Button variant="link" size="inline" onClick={() => void stats.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  // Zero tiles and an empty list say nothing; one clear message and the next step do.
  if (!stats.data || aggregates.length === 0) {
    return (
      <PerformanceEmptyState
        published={pkg.publishedAt !== null}
        scheduled={pkg.scheduledPostsCount}
        onPostForPlayer={onPostForPlayer}
      />
    );
  }

  // bedrock's totals and averages, so the tiles match the All posts dialog; each average leaves out posts
  // that don't report the metric.
  const { totals, averages, reportingCounts } = stats.data;
  const metricTile = (label: string, key: "impressionCount" | "totalReactions" | "commentCount" | "shareCount") => (
    <StatTile
      label={label}
      value={totals[key] ?? 0}
      sub={`avg ${formatCount(averages[key] ?? 0)} per post`}
      subTooltip={reportingCounts && averageTooltip(label, reportingCounts[key])}
    />
  );
  const openFromCard = (post: PostAggregate) =>
    setOpenPost({ postId: post.postId, socialNetworkId: post.primaryNetwork });

  return (
    <div className="flex flex-col gap-3" data-testid="performance-section">
      <div className="flex items-center gap-3">
        <ButtonGroup aria-label="Order posts">
          <Button
            size="xs"
            variant={mode === "recent" ? "secondary" : "outline"}
            aria-pressed={mode === "recent"}
            onClick={() => updateView({ mode: "recent", metric })}
          >
            Recent
          </Button>
          <Button
            size="xs"
            variant={mode === "top" ? "secondary" : "outline"}
            aria-pressed={mode === "top"}
            onClick={() => updateView({ mode: "top", metric })}
          >
            Top performing
          </Button>
        </ButtonGroup>
        {mode === "top" && (
          <div className="text-xs text-slate-600" data-testid="perf-metric">
            <SimpleSelect
              items={METRIC_ITEMS}
              value={metric}
              onValueChange={value => value && updateView({ mode, metric: value as PerfMetric })}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatTile
          className="col-span-2"
          label="Times used"
          value={usage.uses}
          aside={<NetworkChips byNetwork={usage.byNetwork} />}
          footer={
            <>
              {plural(usage.channelPosts, "channel post", "channel posts")} from{" "}
              {plural(usage.players, "player", "players")}
              {pkg.scheduledPostsCount > 0 && (
                <span className="text-slate-400"> · {formatCount(pkg.scheduledPostsCount)} scheduled</span>
              )}
            </>
          }
        />
        {metricTile("Impressions", "impressionCount")}
        {metricTile("Reactions", "totalReactions")}
        {metricTile("Comments", "commentCount")}
        {metricTile("Shares", "shareCount")}
      </div>

      <div className="flex flex-col gap-2">
        {posts.map((post, index) => (
          <PostPerformanceCard
            key={post.postId}
            post={post}
            aspectRatio={pkg.aspectRatio}
            isTop={mode === "top" && index === 0}
            onOpen={openFromCard}
          />
        ))}
      </div>

      <div className="flex flex-col items-end gap-1 pt-1">
        <p className="self-start text-xs text-slate-500">Only posts that went live count.</p>
        <Button variant="link" size="sm" className="px-0" onClick={() => setAllPostsOpen(true)}>
          View all posts
        </Button>
      </div>

      <AllPostsDialog
        open={allPostsOpen}
        onOpenChange={setAllPostsOpen}
        pkg={pkg}
        stats={stats.data}
        loading={stats.isFetching}
        onOpenPost={(postId, socialNetworkId) => {
          setAllPostsOpen(false);
          setReturnToAllPosts(true);
          setOpenPost({ postId, socialNetworkId });
        }}
      />
      {openPost && (
        <PostDetailsDialog
          open
          postId={openPost.postId}
          socialNetworkId={openPost.socialNetworkId}
          onClose={() => {
            setOpenPost(null);
            if (returnToAllPosts) {
              setReturnToAllPosts(false);
              setAllPostsOpen(true);
            }
          }}
        />
      )}
    </div>
  );
}
